import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FormFeedback } from "reactstrap";
import { useAuthenticate, useAppScope } from "..";
import SimpleReactValidator from "simple-react-validator";
import { AuthLayout, ModalLayout, PasswordToggle } from "shared";
import InputControl from "shared/components/InputControl";
import { clearLocalStorage } from "utils";

import loginPageGraphics from "assets/images/login.png";
import logniPasswordGraphics from "assets/images/password.jpg";
import mailRequestGraphics from "assets/images/mail_request.png";
import envelopGraphics from "assets/images/envelop-graphics.svg";
import userAvata from "assets/images/icons/user-avatar.svg";
import warningIcon from "assets/images/warning-graphics.svg";

export const Login = () => {
  const { setIsRemember, isRemember } = useAppScope();
  const {
    authenticateState,
    setAuthenticateState,
    isUserEmailExist,
    authenticateUser,
    modalState,
    onCloseModal,
    resendInvitation,
    sendSignUpToken,
  } = useAuthenticate();
  const { credential, isEmailExist, isInvitation, isSignUp } =
    authenticateState;
  const { userName, password } = credential;
  const [passwordShown, setPasswordShown] = useState(false);
  const [update, forceUpdate] = useState();
  const validator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: forceUpdate },
    })
  );

  const title = "Welcome to " + process.env.REACT_APP_TITLE;
  const isNewUser = modalState.info?.status;
  const errorModalTitle = isNewUser
    ? "We didn’t find that email in our system"
    : "Can’t log in";

  useEffect(() => {
    sessionStorage.clear();
    clearLocalStorage();
  }, []);

  const resendMail = () => {
    if (isSignUp) {
      sendSignUpToken.mutate(userName);
    } else {
      resendInvitation.mutate(userName);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (validator.current.allValid()) {
      if (!isEmailExist) {
        isUserEmailExist.mutate(userName);
        validator.current.hideMessages();
      } else {
        authenticateUser.mutate(credential);
      }
    } else {
      validator.current.showMessages();
      forceUpdate((prev) => prev + 1);
    }
  };

  const onHandleChange = (e) => {
    const { name, value } = e.target;
    setAuthenticateState((draft) => {
      draft.credential[name] = value;
    });
  };

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  const toggleIsRemember = () => {
    setIsRemember((draft) => {
      draft = !draft;
      return draft;
    });
  };

  const handleSignUpToken = () => {
    sendSignUpToken.mutate(userName);
    onCloseModal();
    setAuthenticateState((draft) => {
      draft.isInvitation = true;
      draft.isSignUp = true;
      draft.isEmailExist = false;
      return draft;
    });
  };

  const modalButtonGroup = () => {
    return (
      <div className="button-group d-flex justify-content-center align-items-center">
        <button
          className="btn btn-primary"
          onClick={handleSignUpToken}
          disabled={sendSignUpToken.isPending}
        >
          Create a New Account
        </button>
        <button
          className="btn btn-secondary"
          onClick={onCloseModal}
          disabled={sendSignUpToken.isPending}
        >
          Try again
        </button>
      </div>
    );
  };

  return (
    <>
      <AuthLayout
        leftDivImage={
          isEmailExist
            ? logniPasswordGraphics
            : isInvitation
            ? mailRequestGraphics
            : loginPageGraphics
        }
      >
        {!isEmailExist && !isInvitation && (
          <form>
            <h2>{title}</h2>
            <div className="mb-3">
              <label className="form-label">Enter Email Address</label>
              <InputControl
                type="email"
                name="userName"
                value={userName}
                className="form-control"
                onChange={onHandleChange}
                autoComplete="username"
                disabled={isUserEmailExist.isPending}
                invalid={validator.current.message(
                  "Email Address",
                  userName,
                  "required|email"
                )}
              />
              <FormFeedback>
                {validator.current.message(
                  "Email Address",
                  userName,
                  "required|email"
                )}
              </FormFeedback>
            </div>
            <button
              type="submit"
              onClick={(e) => onSubmit(e)}
              className="btn btn-primary"
              disabled={isUserEmailExist.isPending}
            >
              Continue
            </button>
          </form>
        )}
        {isEmailExist && !isInvitation && (
          <form>
            <h2>{title}</h2>
            <div className="mb-3">
              {userName && (
                <div className="auth-form-user-info d-flex justify-content-start align-items-center">
                  <img src={userAvata} alt="" />
                  <span className="ms-2">{userName}</span>
                </div>
              )}
            </div>
            <p className="form-auth-sm-txt">
              Please enter your registered password
            </p>
            <div className="mb-3 position-relative form-group has-icon-right change_password">
              <label className="form-label">Password</label>
              <InputControl
                type={passwordShown ? "text" : "password"}
                className="form-control"
                name="password"
                placeholder="Password"
                disabled={authenticateUser.isPending}
                value={password}
                onChange={onHandleChange}
                Complete="current-password"
                invalid={validator.current.message(
                  "password",
                  password,
                  "required"
                )}
              />
              <PasswordToggle
                passwordShown={passwordShown}
                togglePassword={togglePassword}
              />
              <FormFeedback>
                {validator.current.message("password", password, "required")}
              </FormFeedback>
            </div>

            <div className="mb-3 d-flex justify-content-between align-items-center">
              <label className="custom-checkbox d-flex justify-content-start align-items-center">
                <input
                  type="checkbox"
                  name="remember"
                  onChange={toggleIsRemember}
                  checked={isRemember}
                />
                <span>Remember me</span>
              </label>
              <Link
                to={`/forgotpassword?email=${userName}`}
                className="form-text-link"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              onClick={(e) => onSubmit(e)}
              className="btn btn-primary"
              disabled={authenticateUser.isPending}
            >
              Log in
            </button>
          </form>
        )}
        {isInvitation && !isEmailExist && (
          <div className="invitation-content">
            <img src={envelopGraphics} alt="" className="block-imgage" />
            <h2>Check your inbox to finish signup</h2>
            <div className="invitation-text">
              <p className="form-auth-sm-txt mb-0">
                We have sent a signup link to you at
                <span> {userName}</span>
              </p>
              <div className="invite-link">
                <span
                  onClick={resendMail}
                  data-disabled={resendInvitation.isPending}
                  className="form-text-link"
                >
                  Resend signup link
                </span>
              </div>
            </div>
          </div>
        )}
      </AuthLayout>

      {modalState.actionFrom === "invalidEmail" && modalState.isOpen && (
        <ModalLayout
          isOpen={modalState.isOpen}
          title={errorModalTitle}
          message={modalState?.info?.message}
          onConfirm={onCloseModal}
          onCancel={onCloseModal}
          modalButtonGroup={isNewUser ? modalButtonGroup() : null}
          icon={warningIcon}
          confirmButtonLabel={isNewUser ? null : "Try Again"}
          modalFooter={
            <p>
              Need help? <Link to="/support">Raise a Support Ticket</Link>
            </p>
          }
        />
      )}
      {modalState.actionFrom === "invalidPassword" && modalState.isOpen && (
        <ModalLayout
          isOpen={modalState.isOpen}
          title={"Incorrect Password"}
          message={modalState?.info}
          onConfirm={onCloseModal}
          onCancel={onCloseModal}
          confirmButtonLabel={"OK"}
          icon={warningIcon}
        />
      )}
    </>
  );
};
